/**
 * AISidebar · 拖拽调宽 hook
 *
 * 对应文档：docs/AI-Photo-Category/TECH_PLAN.md §4.2.2
 *
 * 职责：
 *  · 非受控管理 sidebar 宽度，暴露 mouseDown 事件绑定给 ResizeHandle
 *  · 拖拽期间全局监听 mousemove/mouseup，锁定 body 鼠标样式和文本选择
 *  · mouseup 一次性回调 onEnd(width)，拖拽过程中不回调（避免高频外部更新）
 *
 * 方向约定：sidebar 在页面右侧，手柄在 sidebar 左边缘，
 *          鼠标向左移动 → sidebar 变宽（deltaX = startX - clientX）
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { MouseEvent as ReactMouseEvent } from 'react'

interface UseResizableOptions {
  /** 初始宽度（px） */
  initialWidth: number
  /** 最小宽度（px） · 防止被拖到消失 */
  minWidth: number
  /** 最大宽度（px） · 防止挤占主区域 */
  maxWidth: number
  /** 拖拽结束回调 · 仅 mouseup 触发一次 */
  onEnd?: (width: number) => void
}

interface UseResizableResult {
  /** 当前宽度（px） */
  width: number
  /** 绑定到 ResizeHandle 的 mousedown */
  handleMouseDown: (e: ReactMouseEvent) => void
  /** 是否处于拖拽中 · 供上层关闭 transition 避免拖拽粘滞 */
  isDragging: boolean
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

export function useResizable(options: UseResizableOptions): UseResizableResult {
  const { initialWidth, minWidth, maxWidth, onEnd } = options

  const [width, setWidth] = useState(initialWidth)
  const [isDragging, setIsDragging] = useState(false)

  // 拖拽起点快照 · mousedown 时记录，mousemove 据此算增量
  const dragStartRef = useRef<{ startX: number; startWidth: number } | null>(
    null
  )
  // onEnd 用 ref 持有最新引用，避免作为 useEffect 依赖导致监听频繁重绑
  const onEndRef = useRef(onEnd)
  useEffect(() => {
    onEndRef.current = onEnd
  }, [onEnd])

  // handleMouseDown 的闭包只取一次当时的 width 作为起点，后续 mousemove 自己算增量
  const handleMouseDown = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault()
      dragStartRef.current = { startX: e.clientX, startWidth: width }
      setIsDragging(true)
      // 锁定全局鼠标样式 + 禁选文本，避免拖拽时选中页面文字
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    [width]
  )

  // 拖拽中 · 仅在 isDragging=true 时绑 mousemove/mouseup 全局监听
  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      const start = dragStartRef.current
      if (!start) return
      const deltaX = start.startX - e.clientX
      const next = clamp(start.startWidth + deltaX, minWidth, maxWidth)
      setWidth(next)
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      dragStartRef.current = null
      // 还原 body 样式
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      // 保险清理 · 组件卸载时若正处拖拽，还原 body 样式
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isDragging, minWidth, maxWidth])

  // isDragging 由 true → false 时调一次 onEnd
  // 注意：mouseup 内先 setIsDragging(false)，mousemove 最后一次 setWidth 的新值
  // 已在此时序可见（React 16 事件外 setState 不合批，但独立 render 依次触发，
  // 到这里读到的 width 一定是最后一次 move 的结果）
  const prevDraggingRef = useRef(false)
  useEffect(() => {
    if (prevDraggingRef.current && !isDragging) {
      if (onEndRef.current) onEndRef.current(width)
    }
    prevDraggingRef.current = isDragging
  }, [isDragging, width])

  return { width, handleMouseDown, isDragging }
}
